import React, { useState, useEffect } from 'react'
import { Button, Table } from '@ds/ui'
import { apiFetch, handleApiError } from '../../utils/apiClient';

interface HSMInfoResponse {
  hsmInfo: {
    result: HSMInfoData;
    id: number;
    exception: null | string;
    status: number;
    isCanceled: boolean;
    isCompleted: boolean;
    isCompletedSuccessfully: boolean;
    creationOptions: number;
    asyncState: null | any;
    isFaulted: boolean;
  }
}

interface HSMInfoData {
  driver: string;
  description: string;
  manufacturer: string;
  version: string;
  slots: Array<{
    slotId: number;
    description: string;
    tokenLabel: string;
    tokenModel: string;
    error: null | string;
  }>;
  slotsInUse: number[];
  error: null | string;
}

interface State {
  hsmInfo: HSMInfoData;  // Store just the result data
  loading: boolean;
  error: string | null;
}

interface BannerInfo {
  type: 'success' | 'danger'
  message: string
  retryAction?: () => void
}

interface HSMInfoProps {
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void,
  onHSMConfiguration: (hsmConfiguration: boolean) => void
}

const HSMInfo: React.FC<HSMInfoProps> = ({ onBannerUpdate, onHSMConfiguration }) => {
  const [state, setState] = useState<State>({
    hsmInfo: {
      driver: '',
      description: '',
      manufacturer: '',
      version: '',
      slots: [],
      slotsInUse: [],
      error: null
    },
    loading: true,
    error: null
  })
  const [showSlots, setShowSlots] = useState(false)

  const fetchHSMData = async (isTest: boolean = false) => {
    try {
      const response = await apiFetch('Admin/Home/GetHSMInfo', {
        method: 'GET'
      })

      if (!response.ok) {
        await handleApiError(response);
      }

      const data = await response.json()
      const hsmData = data.hsmInfo.result
      const isConnected = !!hsmData?.driver
      onHSMConfiguration(isConnected)
      if (isTest) {
        onBannerUpdate(isConnected 
          ? { type: 'success', message: 'HSM Connection Successful' }
          : { type: 'danger', message: 'HSM Connection Failed', retryAction: testHSMConnection }
        )
        return isConnected
      }

      setState(prevState => ({
        ...prevState,
        hsmInfo: hsmData,
        loading: false
      }))
      onBannerUpdate(null)
    } catch (err) {
      console.error('Error fetching HSM data:', err)
      if (isTest) {
        onBannerUpdate({ 
          type: 'danger', 
          message: 'HSM Connection Failed', 
          retryAction: testHSMConnection 
        })
        return false
      }
      setState(prevState => ({
        ...prevState,
        error: 'Error fetching HSM data',
        loading: false
      }))
      onBannerUpdate(null)
    } 
  }

  const testHSMConnection = async () => {
    await fetchHSMData(true)
  }

  useEffect(() => {
    fetchHSMData()
  }, [])

  if (state.loading) return <div style={{ paddingLeft: '1rem' }}>Loading HSM data...</div>
  if (state.error){
    if (state.error.includes('Configured')){
      onHSMConfiguration(false)
    } 
    return <div style={{ paddingLeft: '1rem' }}>Error: {state.error}</div>
  }
  if (!state.hsmInfo) return <div style={{ paddingLeft: '1rem' }}>No HSM data available</div>

  return (
    <div>
      <Table columnSettings={[
        { alignment: 'left', width: '3.125rem'},
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '12.5rem' },
        { alignment: 'left', width: '12.5rem' },
      ]}>
        <Table.Body>
          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top', paddingTop: 0, paddingLeft: '1rem' }}>Driver</Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', paddingTop: 0, verticalAlign: 'top' }}>{state.hsmInfo.driver}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top', paddingTop: 0, paddingLeft: '1rem' }}>Description</Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', paddingTop: 0, verticalAlign: 'top' }}>{state.hsmInfo.description}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top', paddingTop: 0, paddingLeft: '1rem' }}>Manufacturer</Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', paddingTop: 0, verticalAlign: 'top' }}>{state.hsmInfo.manufacturer}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top', paddingTop: 0, paddingLeft: '1rem' }}>Version</Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', paddingTop: 0, verticalAlign: 'top' }}>{state.hsmInfo.version}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top', paddingTop: 0, paddingLeft: '1rem',  width: '3.125rem' }}>Slots in use</Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', paddingTop: 0, verticalAlign: 'top' , paddingRight: '0', width: '3.125rem'}}>
              {state.hsmInfo.slotsInUse}
              </Table.Cell>
          </Table.Row>
          <Table.Row>
          <Table.Cell _style={{ width: '5.125rem' , paddingLeft: '1rem'}}>
                <Button
                  kind="secondary"
                  onClick={() => setShowSlots(!showSlots)}
                  text={showSlots ? 'Hide All Slots' : 'Show All Slots'}
                />
            </Table.Cell>
            <Table.Cell>
              <Button
                kind="secondary"
                onClick={testHSMConnection}
                text="Test Connection"
              />
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>

      {showSlots && (
         <Table columnSettings={[{ alignment: 'left' }]}>
          <Table.Header>
            <Table.Row>
              <Table.Cell><strong>ID</strong></Table.Cell>
              <Table.Cell><strong>Description</strong></Table.Cell>
              <Table.Cell><strong>Token Label</strong></Table.Cell>
              <Table.Cell><strong>Token Model</strong></Table.Cell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.hsmInfo.slots.map((slot: any) => (
              <Table.Row key={slot.slotId}>
                <Table.Cell>{slot.slotId}</Table.Cell>
                <Table.Cell>{slot.description}</Table.Cell>
                <Table.Cell>{slot.tokenLabel || '-'}</Table.Cell>
                <Table.Cell>{slot.tokenModel || '-'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  )
}

export default HSMInfo